import { BotPromptChannel, Domain, UserStory } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

//LINK - apps/server/src/API/UserStories/UserStories.Controller.ts

export interface UserStoriesListResponse {
  stories: UserStory[];
  uncommittedPaths: string[];
}

export interface UserStoryUpsertResponse {
  story: UserStory;
}

export interface UserStoryBulkUpsertResponse {
  stories: UserStory[];
}

export interface UserStoryDeleteResponse {
  ok: boolean;
}

export interface UserStorySimilar {
  id: string;
  title: string;
  why: string;
}

export interface UserStoryDraftResponse {
  draft: Partial<UserStory>;
  similarStories: UserStorySimilar[];
}

/**
 * Studio admin client for the user-story catalog. Source of truth: TypeScript
 * files at `libs/types/src/UserStories/<Domain>/index.ts`. The server writes
 * to those files on save (no auto-commit). See plan: ancient-percolating-lantern.md.
 */
export class UserStoriesAPI {
  static async list(): Promise<UserStoriesListResponse> {
    return APIService.Server.get<UserStoriesListResponse>("/user-stories");
  }

  static async getOne(id: string): Promise<UserStory | undefined> {
    return APIService.Server.get<UserStory | undefined>(`/user-stories/${id}`);
  }

  static async upsert(story: UserStory): Promise<UserStoryUpsertResponse> {
    return APIService.Server.put<UserStoryUpsertResponse>(`/user-stories/${story.id}`, story);
  }

  /**
   * Bulk-upsert. The server batches all writes into one file rewrite per
   * affected domain plus a single markdown regen — used by studio's
   * bulk-status / bulk-category actions to avoid triggering nx restarts
   * mid-batch.
   */
  static async bulkUpsert(updates: UserStory[]): Promise<UserStoryBulkUpsertResponse> {
    return APIService.Server.patch<UserStoryBulkUpsertResponse>("/user-stories/bulk", { updates });
  }

  static async remove(id: string): Promise<UserStoryDeleteResponse> {
    return APIService.Server.delete<UserStoryDeleteResponse>(`/user-stories/${id}`);
  }

  /**
   * Draft a new story from prose using Claude Opus. Returns a `Partial<UserStory>`
   * the studio loads into the Add modal for review, plus up to 5 existing
   * stories that look semantically similar (potential duplicates).
   */
  static async draft(description: string, domain: Domain): Promise<UserStoryDraftResponse> {
    return APIService.Server.post<UserStoryDraftResponse>("/user-stories/draft", { description, domain });
  }

  /**
   * Persist a (story × channel) eval-run summary back into the user-story
   * source. Called by the bot's eval runner after each case (dev-only —
   * server `applyEvalResult` writes to disk).
   */
  static async applyEvalResult(
    id: string,
    body: {
      channel: BotPromptChannel;
      runId: number;
      passed: boolean;
      score: number;
      ranAt: string;
      variableKeys?: string[];
      toolNames?: string[];
      promptSectionIds?: string[];
    }
  ): Promise<UserStoryUpsertResponse> {
    return APIService.Server.post<UserStoryUpsertResponse>(`/user-stories/${id}/eval-result`, body);
  }

  /** Bump `channelStatus[channel]` to `"live"` for a story (studio's Mark-live). */
  static async promoteChannel(id: string, channel: BotPromptChannel): Promise<UserStoryUpsertResponse> {
    return APIService.Server.post<UserStoryUpsertResponse>(`/user-stories/${id}/promote-channel`, { channel });
  }
}
